




import {defineComponent, onBeforeUnmount, onMounted, ref} from "@vue/composition-api"
import io from 'socket.io-client'
import Avatar from "@/rooms/Avatar.vue"

type Socket = typeof io.Socket

const SERVERS = {'iceServers': [{'urls': 'stun:stun.l.google.com:19302'}]}

export default defineComponent({
  name: "Peer",
  components: {Avatar},
  props: {
    peerId: {
      type: String,
      required: true
    },
    localStream: {
      type: MediaStream,
      required: true
    },
    socket: {
      type: Object as () => Socket,
      required: true
    },
    offer: {
      type: Object as () => (RTCSessionDescriptionInit | null),
      required: false,
      default: null
    }
  },
  setup(props) {
    const connection = new RTCPeerConnection(SERVERS)
    const remoteStream = ref<MediaStream>()
    const initiator = props.offer === null

    const tracks: MediaStreamTrack[] = []

    console.log(`Start peer component ${props.peerId}...`)

    connection.onicecandidate = (event: RTCPeerConnectionIceEvent) => {
      if (event.candidate) {
        console.log(`Send candidate to peer ${props.peerId}`)
        props.socket.emit('candidate', {
          candidate: event.candidate,
          toId: props.peerId
        })
      } else {
        console.log('No more candidates')
      }
    }

    connection.onicecandidateerror = (event: RTCPeerConnectionIceErrorEvent) => {
      console.error(event.errorText)
    }

    connection.oniceconnectionstatechange = () => {
      console.log(`Connection ICE state changed ${connection.connectionState} -- ${connection.iceConnectionState}`)
    }

    connection.addEventListener('track', async ({track}: RTCTrackEvent) => {
      console.log(`Receive remote track ${track.kind}`)
      tracks.push(track)
    })

    props.socket.on('answer:' + props.peerId, async (description: RTCSessionDescriptionInit) => {
      console.log(`Receive answer from peer ${props.peerId}`)
      const remoteDescription = new RTCSessionDescription(description)
      await connection.setRemoteDescription(remoteDescription)
    })

    props.socket.on('candidate:' + props.peerId, (candidate: RTCIceCandidate) => {
      console.log(`Receive candidate from peer ${props.peerId}`)
      connection.addIceCandidate(candidate).catch(console.error)
    })

    props.localStream.getTracks().forEach(track => {
      connection.addTrack(track, props.localStream)
    })

    onMounted(async () => {
      connection.onconnectionstatechange = () => {
        console.log(`Connection state changed ${connection.connectionState} -- ${connection.iceConnectionState}`)
        if (connection.connectionState === 'connected') {
          remoteStream.value = new MediaStream(tracks)
        }
      }

      if (initiator) {
        console.log(`Send offer to new peer ${props.peerId}`)
        const offer: RTCSessionDescriptionInit = await connection.createOffer()
        await connection.setLocalDescription(offer)
        props.socket.emit('offer', {offer, toId: props.peerId})
      } else {
        console.log(`Receive offer from peer ${props.peerId}`)
        const remoteDescription = new RTCSessionDescription(props.offer!!)
        await connection.setRemoteDescription(remoteDescription)
        const answer: RTCSessionDescriptionInit = await connection.createAnswer()
        await connection.setLocalDescription(answer)
        console.log(`Send answer to peer ${props.peerId}`)
        props.socket.emit('answer', {description: answer, toId: props.peerId})
      }
    })

    onBeforeUnmount(() => {
      connection.close()
    })

    return {remoteStream}
  }
})
